<template>
  <navbar btnBackground="bg-gradient-danger" />
  <div
    class="page-header align-items-start min-vh-100"
    :style="{ backgroundImage: `url(${capBackground})`, backgroundSize: 'cover', backgroundPosition: 'center' }"
  >
    <span class="mask" style="background: rgba(0, 0, 0, 0.5);"></span>

    <div class="container my-auto">
      <div class="row">
        <div class="col-lg-4 col-md-8 col-12 mx-auto">
          <div class="card z-index-0 fadeIn3 fadeInBottom">
            <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
              <div class="border-radius-lg py-3 pe-1" style="background-color: #b03a45;">
                <h4 class="text-white font-weight-bolder text-center mt-2 mb-0">Sign in</h4>
              </div>
            </div>

            <div class="card-body">
              <form class="text-start mt-3" @submit.prevent="handleLogin">
                <div class="mb-3">
                  <material-input id="email" type="email" label="Email" v-model="form.email" />
                </div>
                <div class="mb-3">
                  <div class="position-relative">
                    <material-input 
                      id="password" 
                      :type="showPassword ? 'text' : 'password'" 
                      label="Password" 
                      v-model="form.password" 
                    />
                    <button
                      type="button"
                      class="btn position-absolute end-0 top-50 translate-middle-y me-2"
                      style="background: none; border: none; color: #6c757d; padding: 0; z-index: 10;"
                      @click="showPassword = !showPassword"
                    >
                      <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                    </button>
                  </div>
                </div>
                <material-switch id="rememberMe" v-model="form.rememberMe">
                  Remember me
                </material-switch>

                <div class="text-center">
                  <material-button
                    class="text-white font-weight-bolder text-center my-4 mb-2"
                    style="background-color: #b03a45;"
                    fullWidth
                    type="submit"
                  >
                    Sign in
                  </material-button>
                </div>

                <p class="mt-4 text-sm text-center">
                  Don't have an account?
                  <router-link :to="{ name: 'SignUp' }" class="font-weight-bold" style="color: #b03a45;">
                    Sign up
                  </router-link>
                </p>
              </form>

              <p v-if="error" class="text-danger text-center mt-2">{{ error }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <footer class="footer position-absolute bottom-2 py-2 w-100">
      <div class="container">
        <div class="row align-items-center justify-content-lg-between"></div>
      </div>
    </footer>
  </div>
</template>

<style scoped>
/* Override ALL green borders with red theme - MAXIMUM PRIORITY */
.card {
  border: 1px solid #b03a45 !important;
}

/* Target all possible input border states */
:deep(.form-control) {
  border-color: #b03a45 !important;
}

:deep(.input-group .form-control) {
  border-color: #b03a45 !important;
}

:deep(.input-group.is-focused .form-control) {
  border-color: #b03a45 !important;
}

:deep(.input-group.focused .form-control) {
  border-color: #b03a45 !important;
}

/* CRITICAL: Focus state - when clicking to write */
:deep(.form-control:focus) {
  border-color: #b03a45 !important;
  box-shadow: 0 0 0 0.1rem rgba(176, 58, 69, 0.25) !important;
  outline: 0 !important;
}

/* Override any success/green states */
:deep(.form-control.is-valid) {
  border-color: #b03a45 !important;
}

:deep(.form-control:valid) {
  border-color: #b03a45 !important;
}

/* ULTRA SPECIFIC: All possible focus combinations */
:deep(.input-group:focus .form-control) {
  border-color: #b03a45 !important;
}

:deep(.input-group:focus-within .form-control) {
  border-color: #b03a45 !important;
}

:deep(.input-group:focus .form-control:focus) {
  border-color: #b03a45 !important;
}

/* Global override for any green borders */
:deep(*) {
  border-color: #b03a45 !important;
}

:deep(.form-control):not(.is-invalid):not(:focus) {
  border-color: #b03a45 !important;
}

:deep(.input-group):not(.is-invalid):not(:focus) .form-control {
  border-color: #b03a45 !important;
}

/* KILL ALL GREEN BORDERS - NUCLEAR OPTION */
:deep(.form-control:focus) {
  border-color: #b03a45 !important;
  border: 1px solid #b03a45 !important;
  box-shadow: 0 0 0 0.1rem rgba(176, 58, 69, 0.25) !important;
  outline-color: #b03a45 !important;
}

:deep(.input-group:focus-within) {
  border-color: #b03a45 !important;
}

:deep(.input-group:focus-within .form-control) {
  border-color: #b03a45 !important;
}
</style>

<script>
import Navbar from "@/examples/PageLayout/Navbar.vue";
import MaterialInput from "@/components/MaterialInput.vue";
import MaterialSwitch from "@/components/MaterialSwitch.vue";
import MaterialButton from "@/components/MaterialButton.vue";
import { mapMutations } from "vuex";
import capBackground from "@/assets/img/cap.png";
import authService from "@/services/authService";

/** Util: extrait un rôle lisible quel que soit le format renvoyé par l'API */
function resolveRole(user) {
  // 1) user.role = 'admin' | 'employee'
  if (typeof user?.role === "string") return user.role.toLowerCase();

  // 2) spatie/laravel-permission: user.roles = ['admin'] ou [{ name: 'admin' }]
  if (Array.isArray(user?.roles) && user.roles.length) {
    const first = user.roles[0];
    const name = typeof first === "string" ? first : first?.name;
    if (name) return String(name).toLowerCase();
  }
  return null;
}

export default {
  name: "sign-in",
  components: { Navbar, MaterialInput, MaterialSwitch, MaterialButton },
  data() {
    return {
      capBackground,
      error: "",
      showPassword: false,
      form: { email: "", password: "", rememberMe: false },
    };
  },
  methods: {
    ...mapMutations(["toggleEveryDisplay", "toggleHideConfig"]),

    async handleLogin() {
      this.error = "";
      try {
        const response = await authService.login({
          email: this.form.email,
          password: this.form.password,
        });

        const { token, user } = response.data;

        // Normaliser et stocker le rôle
        const role = resolveRole(user);
        const userToStore = { ...user, role };
        localStorage.setItem("token", token);
        localStorage.setItem("user", JSON.stringify(userToStore));

        // 👉 Redirection selon le rôle
        if (role === "admin") {
          this.$router.push({ name: "Dashboard" });
        } else if (role === "employee") {
          // Assure-toi que cette route existe: { name: 'EmployeeProfile', path: '/employee/profile' }
          this.$router.push({ name: "EmployeeProfile" });
        } else {
          // Rôle inconnu → fallback
          this.$router.push({ name: "Dashboard" });
        }
      } catch (error) {
        if (error.response?.status === 401) {
          this.error = "Invalid credentials. Please try again.";
        } else if (error.response?.status === 422) {
          this.error = "Please fill in all required fields correctly.";
        } else {
          this.error = "Login failed. Please check your connection.";
        }
      }
    },
  },
  mounted() {
    this.toggleEveryDisplay();
    this.toggleHideConfig();
  },
  beforeUnmount() {
    this.toggleEveryDisplay();
    this.toggleHideConfig();
  },
};
</script>
